/**
 * Lab internal quality control (medopd-docs 113) — control materials (lot + level + target mean/SD), runs judged by
 * the Westgard rules on the server, the release hold, and the Levey-Jennings chart data. Backs /lab/qc (LabQcController).
 * `list` / `logRun` are the original free-form endpoints, kept for compatibility.
 */
import { fetchApi } from '../client';
import type { PagedResponse } from '../../../types/pagination';

export interface LabQcRunInput {
  labId?: string;
  testDefinitionId?: string;
  testName: string;
  controlLevel: string;
  controlLotNumber?: string;
  expectedLow?: number;
  expectedHigh?: number;
  observedValue: number;
  unit?: string;
  correctiveAction?: string;
  notes?: string;
}

export type QcLevel = 'LOW' | 'NORMAL' | 'HIGH';
export type QcStatus = 'PASS' | 'WARNING' | 'FAIL';

export interface QcMaterial {
  id: string;
  testDefinitionId?: string | null;
  testName: string;
  parameterName?: string | null;
  controlLevel: QcLevel;
  materialName: string;
  lotNumber: string;
  expiryDate?: string | null;
  targetMean: number;
  targetSd: number;
  unit?: string | null;
  active: boolean;
  low2Sd?: number;
  high2Sd?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface QcMaterialInput {
  testDefinitionId?: string | null;
  testName?: string;
  parameterName?: string;
  controlLevel: QcLevel;
  materialName: string;
  lotNumber: string;
  expiryDate: string;
  targetMean: number;
  targetSd: number;
  unit?: string;
  active?: boolean;
}

export interface QcRun {
  id: string;
  materialId?: string | null;
  testDefinitionId?: string | null;
  testName: string;
  controlLevel: string;
  controlLotNumber?: string | null;
  expectedLow?: number | null;
  expectedHigh?: number | null;
  observedValue: number;
  zScore?: number | null;
  unit?: string | null;
  /** PASS / WARNING / FAIL — computed by the server. */
  result: QcStatus;
  ruleViolations?: string | null;
  correctiveAction?: string | null;
  notes?: string | null;
  performedBy: string;
  performedAt: string;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
}

export interface QcRunRow {
  run: QcRun;
  parameterName?: string | null;
  materialName?: string | null;
}

export interface QcTodayItem {
  material: QcMaterial;
  todayRuns: QcRun[];
  status: QcStatus | null;
  done: boolean;
  onHold: boolean;
  holdRunId: string | null;
  expired: boolean;
  expiresSoon: boolean;
}

export interface QcHold {
  materialId: string;
  testDefinitionId?: string | null;
  testName: string;
  parameterName?: string | null;
  controlLevel: string;
  materialName: string;
  lotNumber: string;
  runId: string;
  performedAt: string;
  observedValue: number;
  zScore?: number | null;
  rules?: string | null;
}

export interface QcChartPoint {
  runId: string;
  performedAt: string;
  value: number;
  z: number | null;
  status: QcStatus;
  rules?: string | null;
  lot?: string | null;
  resolved: boolean;
}

export interface QcChart {
  material: QcMaterial;
  points: QcChartPoint[];
}

export interface QcRunsQuery {
  page: number;
  size: number;
  materialId?: string;
  testName?: string;
  status?: QcStatus | '';
  from?: string;
  to?: string;
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const labQcModule = {
  list: (testName?: string) =>
    fetchApi<any[]>(`/api/lab/qc${testName ? `?testName=${encodeURIComponent(testName)}` : ''}`),

  logRun: (data: LabQcRunInput) =>
    fetchApi<any>('/api/lab/qc', { method: 'POST', body: JSON.stringify(data) }),

  // ── Control materials ──
  listMaterials: (includeInactive = false) =>
    fetchApi<QcMaterial[]>(`/api/lab/qc/materials${includeInactive ? '?includeInactive=true' : ''}`),
  createMaterial: (data: QcMaterialInput) =>
    fetchApi<QcMaterial>('/api/lab/qc/materials', { method: 'POST', body: JSON.stringify(data) }),
  updateMaterial: (id: string, data: QcMaterialInput) =>
    fetchApi<QcMaterial>(`/api/lab/qc/materials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deactivateMaterial: (id: string) =>
    fetchApi<QcMaterial>(`/api/lab/qc/materials/${id}`, { method: 'DELETE' }),
  chart: (materialId: string, days = 30) =>
    fetchApi<QcChart>(`/api/lab/qc/materials/${materialId}/chart?days=${days}`),

  // ── Runs ──
  runs: (q: QcRunsQuery) =>
    fetchApi<PagedResponse<QcRunRow>>(`/api/lab/qc/runs${qs({ ...q })}`),
  logMaterialRun: (data: { materialId: string; observedValue: number; performedAt?: string; notes?: string }) =>
    fetchApi<{ run: QcRun; clearedHold: boolean }>('/api/lab/qc/runs', { method: 'POST', body: JSON.stringify(data) }),
  resolveRun: (runId: string, note: string) =>
    fetchApi<QcRun>(`/api/lab/qc/runs/${runId}/resolve`, { method: 'POST', body: JSON.stringify({ note }) }),

  // ── Board ──
  today: () => fetchApi<QcTodayItem[]>('/api/lab/qc/today'),
  holds: () => fetchApi<QcHold[]>('/api/lab/qc/holds'),
};
